import type {
  CreateTournamentRequest,
  HeldTournament,
  PatchTournamentRequest,
  TournamentSessionRequest,
} from '@brush/api-client';

/**
 * What a file would change about a tournament the room already has.
 *
 * A file re-run against a schedule it has already been imported into is the ordinary case, not a
 * mistake: an operator re-exports it after fixing three start times and imports it again. So an
 * internal ID that already exists is a question — *has anything moved?* — rather than a collision.
 *
 * Two rules shape everything here:
 *
 *  - **Only what the template declares is compared, and only what differs is written.** A template
 *    with no `status` column must not report a status difference and must not overwrite one. The
 *    patch carries the changed fields and nothing else, so an import can never quietly reset a
 *    field the file has no opinion about.
 *  - **Nothing is deleted.** A tag the file omits stays; a platform tag especially, since a room
 *    is not allowed to remove one at all. The schedule is the one thing replaced wholesale — it is
 *    a single fact about the tournament rather than a collection of separate ones — and the change
 *    says so in sittings so it is never a surprise.
 */

/** One difference, in the words the review shows it in. */
export interface FieldChange {
  label: string;
  from: string;
  to: string;
}

export interface TournamentDiff {
  changes: FieldChange[];
  /** Only the fields that differ. Empty when the tournament already matches the file. */
  patch: Partial<PatchTournamentRequest>;
}

/**
 * Which fields the template has an opinion about. Date, time and buy-in are absent because they
 * are required columns — a template that can import at all declares all three.
 */
export interface GovernedFields {
  name: boolean;
  guarantee: boolean;
  guaranteeUnits: boolean;
  eventNumber: boolean;
  status: boolean;
  tags: boolean;
  /** `session_day` or `is_multi_day` — either one makes the file authoritative on the schedule. */
  schedule: boolean;
}

const money = (value: number) => `$${value.toLocaleString()}`;

const guaranteeText = (amount: number | null, units: string | null) =>
  amount === null ? 'none' : units ? `${amount.toLocaleString()} ${units}` : money(amount);

const scheduleText = (isMultiDay: boolean, count: number) =>
  !isMultiDay
    ? 'single day'
    : count === 0
      ? 'multi-day, to be announced'
      : `${count} sitting${count === 1 ? '' : 's'}`;

const text = (value: string | null) => (value === null || value === '' ? 'none' : value);

/** Case-insensitive, order-insensitive: a reordered tag list is not a change. */
const tagKey = (tags: readonly string[]) =>
  [...tags].map((t) => t.toLowerCase()).sort().join(' ');

/** The schedule as one comparable string. Sorted, because file order and stored order differ. */
const sittingKey = (
  sessions: readonly { day: number; sessionName: string | null; date: string; time: string }[],
) =>
  [...sessions]
    .map((s) => `${s.day}|${(s.sessionName ?? '').toUpperCase()}|${s.date}|${s.time}`)
    .sort()
    .join(';');

export function diffTournament({
  existing,
  next,
  governs,
  platformTags,
}: {
  existing: HeldTournament;
  next: CreateTournamentRequest;
  governs: GovernedFields;
  platformTags: readonly string[];
}): TournamentDiff {
  const changes: FieldChange[] = [];
  const patch: Partial<PatchTournamentRequest> = {};

  const note = (label: string, from: string, to: string) => changes.push({ label, from, to });

  if (governs.name && existing.name !== next.name) {
    note('name', existing.name, next.name);
    patch.name = next.name;
  }

  if (existing.date !== next.date) {
    note('date', existing.date, next.date);
    patch.date = next.date;
  }

  if (existing.time !== next.time) {
    note('start time', existing.time, next.time);
    patch.time = next.time;
  }

  // The null check is the contract's, not this file's: `buyIn` became nullable so that an absent
  // buy-in is distinguishable from a freeroll. It is never null here — `buy_in` is one of the five
  // columns a template must declare to import at all, so `buildPlan` has already refused the file
  // — and the guard says so rather than asserting it away, since a null would otherwise report a
  // change from $300 to nothing on a row the file never spoke about.
  if (next.buyIn !== null && existing.buyIn !== next.buyIn) {
    note('buy-in', money(existing.buyIn), money(next.buyIn));
    patch.buyIn = next.buyIn;
  }

  // The amount and its units are one fact to an operator — "$50,000" against "5 Seats" — so they
  // are reported together even though the contract carries them apart.
  if (governs.guarantee || governs.guaranteeUnits) {
    const nextAmount = governs.guarantee ? next.guarantee : existing.guarantee;
    const nextUnits = governs.guaranteeUnits ? next.guaranteeUnits : existing.guaranteeUnits;

    const amountMoved = existing.guarantee !== nextAmount;
    const unitsMoved = (existing.guaranteeUnits ?? '') !== (nextUnits ?? '');

    if (amountMoved || unitsMoved) {
      note(
        'guarantee',
        guaranteeText(existing.guarantee, existing.guaranteeUnits),
        guaranteeText(nextAmount, nextUnits),
      );

      // Reported together, written apart: a changed amount alongside units that were already
      // right should not also send a units write that does nothing.
      if (governs.guarantee && amountMoved) {
        // Null already means "leave unchanged" on a PATCH, so removing a guarantee needs the flag.
        if (nextAmount === null) patch.clearGuarantee = true;
        else patch.guarantee = nextAmount;
      }
      // An empty string clears an optional string; null would mean "leave unchanged".
      if (governs.guaranteeUnits && unitsMoved) patch.guaranteeUnits = nextUnits ?? '';
    }
  }

  if (governs.eventNumber && (existing.eventNumber ?? '') !== (next.eventNumber ?? '')) {
    note('event number', text(existing.eventNumber), text(next.eventNumber));
    patch.eventNumber = next.eventNumber ?? '';
  }

  if (governs.status && next.status !== null && existing.status !== next.status) {
    note('status', existing.status, next.status);
    patch.status = next.status;
  }

  // A blank tags cell means the file said nothing, which on an existing tournament is "leave them
  // alone" rather than "remove them all".
  if (governs.tags && next.tags !== null) {
    const fileTags = next.tags;
    const has = (tags: readonly string[], tag: string) =>
      tags.some((t) => t.toLowerCase() === tag.toLowerCase());

    // Rooms cannot remove a platform tag from a tournament, so a file that omits one does not
    // get to either. Carried across rather than reported as a difference.
    const keptPlatform = existing.tags.filter(
      (t) => has(platformTags, t) && !has(fileTags, t),
    );
    const merged = [...fileTags, ...keptPlatform];

    if (tagKey(existing.tags) !== tagKey(merged)) {
      // Both sides in full, like every other change here. A +/− delta reads as a different kind
      // of value from the "before" beside it, and the lists are short enough to just show.
      note(
        'tags',
        existing.tags.length > 0 ? existing.tags.join(', ') : 'none',
        merged.length > 0 ? merged.join(', ') : 'none',
      );
      patch.tags = merged;
    }
  }

  if (governs.schedule) {
    const nextSessions = next.sessions ?? [];
    const scheduleMoved =
      existing.isMultiDay !== next.isMultiDay ||
      // A multi-day file with no sittings listed says nothing about the schedule, so an existing
      // one is left standing rather than emptied.
      (next.isMultiDay &&
        nextSessions.length > 0 &&
        sittingKey(existing.sessions) !== sittingKey(nextSessions));

    if (scheduleMoved) {
      note(
        'schedule',
        scheduleText(existing.isMultiDay, existing.sessions.length),
        scheduleText(next.isMultiDay, nextSessions.length),
      );
      patch.isMultiDay = next.isMultiDay;
      patch.sessions =
        next.isMultiDay && nextSessions.length > 0 ? withStoredNames(existing, nextSessions) : null;
    }
  }

  return { changes, patch };
}

/**
 * Keeps whatever a room has called a sitting it already has.
 *
 * A session's name is stored outright rather than inherited, precisely so a room can reword
 * "Day 1A" and have it stay reworded. Every row of a session file carries the parent's name, so
 * sending those names back would undo that on every re-import.
 */
function withStoredNames(
  existing: HeldTournament,
  sessions: readonly TournamentSessionRequest[],
): TournamentSessionRequest[] {
  const key = (day: number, sessionName: string | null) =>
    `${day}|${(sessionName ?? '').toUpperCase()}`;
  const stored = new Map(existing.sessions.map((s) => [key(s.day, s.sessionName), s.name]));

  return sessions.map((s) => ({
    ...s,
    name: stored.get(key(s.day, s.sessionName)) ?? s.name,
  }));
}
